import { ResponseError } from '@backstage/errors';
import {
  TerraformPipelinesApi,
  TerraformStack,
  TerraformEnvironment,
  TerraformRun,
  TriggerTerraformOptions,
} from './TerraformPipelinesApi';
import { WorkflowPhase } from './types';

interface FetchApi {
  fetch: (input: RequestInfo, init?: RequestInit) => Promise<Response>;
}

interface DiscoveryApi {
  getBaseUrl(pluginId: string): Promise<string>;
}

const IAC_TEMPLATE_PATTERN = /^(terraform-|terragrunt-)/;
const NS_CACHE_TTL = 5 * 60 * 1000;

export function envSortKey(env: string): number {
  if (/^(alpha|gamma|devops)$/i.test(env)) return 0;
  if (env === 'staging' || env.endsWith('stage')) return 1;
  if (env === 'production' || env.endsWith('prod')) return 2;
  return 3;
}

export function humanizeEnv(env: string): string {
  const stageMatch = env.match(/^([a-z]{2})\d*stage$/);
  if (stageMatch) return `Staging ${stageMatch[1].toUpperCase()}`;
  const prodMatch = env.match(/^([a-z]{2})\d*prod$/);
  if (prodMatch) return `Prod ${prodMatch[1].toUpperCase()}`;
  if (env === 'staging') return 'Staging US';
  if (env === 'production') return 'Prod US';
  return env.charAt(0).toUpperCase() + env.slice(1);
}

export class TerraformPipelinesClient implements TerraformPipelinesApi {
  private readonly fetchApi: FetchApi;
  private readonly discoveryApi: DiscoveryApi;
  private nsCache: { promise: Promise<string[]>; ts: number } | null = null;

  constructor(deps: { fetchApi: FetchApi; discoveryApi: DiscoveryApi }) {
    this.fetchApi = deps.fetchApi;
    this.discoveryApi = deps.discoveryApi;
  }

  private async base(): Promise<string> {
    const proxy = await this.discoveryApi.getBaseUrl('proxy');
    return `${proxy}/argo-workflows`;
  }

  private async resolveIaCNamespaces(): Promise<string[]> {
    if (this.nsCache && Date.now() - this.nsCache.ts < NS_CACHE_TTL) {
      return this.nsCache.promise;
    }
    const promise = this.doResolveIaCNamespaces();
    this.nsCache = { promise, ts: Date.now() };
    promise.catch(() => { this.nsCache = null; });
    return promise;
  }

  private async doResolveIaCNamespaces(): Promise<string[]> {
    const base = await this.base();
    const res = await this.fetchApi.fetch(`${base}/api/v1/workflow-templates/`);
    if (!res.ok) return ['terraform'];
    const json = (await res.json()) as { items?: { metadata: { namespace: string; name: string } }[] };
    const items = json.items ?? [];
    const iacNamespaces = new Set<string>();
    for (const t of items) {
      if (IAC_TEMPLATE_PATTERN.test(t.metadata.name)) {
        iacNamespaces.add(t.metadata.namespace);
      }
    }
    return [...iacNamespaces].sort();
  }

  async listStacks(): Promise<TerraformStack[]> {
    const base = await this.base();
    const namespaces = await this.resolveIaCNamespaces();
    const allItems: ArgoWorkflowObj[] = [];
    await Promise.all(
      namespaces.map(async ns => {
        const url = new URL(`${base}/api/v1/workflows/${encodeURIComponent(ns)}`);
        url.searchParams.set('listOptions.limit', '200');
        const res = await this.fetchApi.fetch(url.toString());
        if (!res.ok) return;
        const json = (await res.json()) as { items?: ArgoWorkflowObj[] };
        allItems.push(...(json.items ?? []));
      }),
    );

    const stackMap = new Map<string, Map<string, TerraformRun[]>>();

    for (const wf of allItems) {
      const module = wf.metadata.labels?.['terraform.io/module'] ?? extractModule(wf);
      if (!module) continue;
      const env = wf.metadata.labels?.['terraform.io/environment']
        ?? extractEnvFromParams(wf)
        ?? 'unknown';
      const action = deriveAction(wf);

      const run: TerraformRun = {
        workflowName: wf.metadata.name,
        namespace: wf.metadata.namespace,
        uid: wf.metadata.uid,
        phase: wf.status?.phase ?? 'Unknown',
        action,
        startedAt: wf.status?.startedAt,
        finishedAt: wf.status?.finishedAt,
        commitSha: wf.metadata.labels?.['terraform.io/commit'],
      };

      if (!stackMap.has(module)) stackMap.set(module, new Map());
      const envMap = stackMap.get(module)!;
      if (!envMap.has(env)) envMap.set(env, []);
      envMap.get(env)!.push(run);
    }

    const stacks: TerraformStack[] = [];
    for (const [module, envMap] of stackMap) {
      const environments: TerraformEnvironment[] = [];
      for (const [envName, runs] of envMap) {
        runs.sort((a, b) => cmpDate(b.startedAt, a.startedAt));
        environments.push({ name: envName, latestRun: runs[0], runs });
      }
      environments.sort((a, b) => {
        const tierA = envSortKey(a.name);
        const tierB = envSortKey(b.name);
        if (tierA !== tierB) return tierA - tierB;
        return a.name.localeCompare(b.name);
      });
      stacks.push({ module, environments });
    }

    stacks.sort((a, b) => a.module.localeCompare(b.module));
    return stacks;
  }

  async getStackRuns(module: string, environment?: string): Promise<TerraformRun[]> {
    const base = await this.base();
    const namespaces = await this.resolveIaCNamespaces();
    const ns = namespaces[0] ?? 'terraform';
    const url = new URL(`${base}/api/v1/workflows/${encodeURIComponent(ns)}`);
    let selector = `terraform.io/module=${module}`;
    if (environment) selector += `,terraform.io/environment=${environment}`;
    url.searchParams.set('listOptions.labelSelector', selector);
    url.searchParams.set('listOptions.limit', '50');

    const res = await this.fetchApi.fetch(url.toString());
    if (!res.ok) throw await ResponseError.fromResponse(res);
    const json = (await res.json()) as { items?: ArgoWorkflowObj[] };
    return (json.items ?? []).map(wf => ({
      workflowName: wf.metadata.name,
      namespace: wf.metadata.namespace,
      uid: wf.metadata.uid,
      phase: wf.status?.phase ?? 'Unknown',
      action: deriveAction(wf),
      startedAt: wf.status?.startedAt,
      finishedAt: wf.status?.finishedAt,
      commitSha: wf.metadata.labels?.['terraform.io/commit'],
    })).sort((a, b) => cmpDate(b.startedAt, a.startedAt));
  }

  async triggerPlan(opts: TriggerTerraformOptions): Promise<{ namespace: string; name: string }> {
    return this.submit(opts, 'plan');
  }

  async triggerApply(opts: TriggerTerraformOptions): Promise<{ namespace: string; name: string }> {
    return this.submit(opts, 'apply');
  }

  private async submit(
    opts: TriggerTerraformOptions,
    action: 'plan' | 'apply',
  ): Promise<{ namespace: string; name: string }> {
    const base = await this.base();
    const namespaces = await this.resolveIaCNamespaces();
    const ns = namespaces[0] ?? 'terraform';
    const url = `${base}/api/v1/workflows/${encodeURIComponent(ns)}`;
    const body = {
      workflow: {
        metadata: {
          namespace: ns,
          generateName: `${opts.module}-${action}-`,
          labels: {
            'terraform.io/module': opts.module,
            'terraform.io/environment': opts.environment,
            'terraform.io/action': action,
            'app.kubernetes.io/managed-by': 'backstage',
          },
        },
        spec: {
          workflowTemplateRef: { name: 'terraform-module-release' },
          arguments: {
            parameters: [
              { name: 'repo_url', value: `https://github.com/MindTouch/${opts.module}.git` },
              { name: 'environment', value: opts.environment },
              { name: 'action', value: action },
              ...(opts.parameters
                ? Object.entries(opts.parameters).map(([name, value]) => ({ name, value }))
                : []),
            ],
          },
        },
      },
    };
    const res = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw await ResponseError.fromResponse(res);
    const json = (await res.json()) as { metadata: { namespace: string; name: string } };
    return { namespace: json.metadata.namespace, name: json.metadata.name };
  }
}

interface ArgoWorkflowObj {
  metadata: {
    namespace: string;
    name: string;
    uid: string;
    labels?: Record<string, string>;
  };
  spec?: {
    workflowTemplateRef?: { name?: string };
    arguments?: { parameters?: { name: string; value?: string }[] };
  };
  status?: {
    phase?: WorkflowPhase;
    startedAt?: string;
    finishedAt?: string;
  };
}

function extractModule(wf: ArgoWorkflowObj): string | undefined {
  const params = wf.spec?.arguments?.parameters;
  if (!params) return undefined;
  const moduleParam = params.find(p => p.name === 'module')?.value;
  if (moduleParam) return moduleParam;
  const repoUrl = params.find(p => p.name === 'repo_url')?.value;
  if (!repoUrl) return undefined;
  const match = repoUrl.match(/\/([^/]+?)(?:\.git)?$/);
  return match?.[1];
}

function extractEnvFromParams(wf: ArgoWorkflowObj): string | undefined {
  const params = wf.spec?.arguments?.parameters;
  if (!params) return undefined;
  return params.find(p => p.name === 'cloud_name')?.value
    ?? params.find(p => p.name === 'env')?.value
    ?? params.find(p => p.name === 'alias')?.value
    ?? params.find(p => p.name === 'environment')?.value;
}

function deriveAction(wf: ArgoWorkflowObj): 'plan' | 'apply' | 'release' {
  const label = wf.metadata.labels?.['terraform.io/action'];
  if (label === 'plan' || label === 'apply') return label;
  const tmpl = wf.spec?.workflowTemplateRef?.name ?? '';
  if (tmpl.includes('apply')) return 'apply';
  if (tmpl.includes('plan')) return 'plan';
  return 'release';
}

function cmpDate(a?: string, b?: string): number {
  return (a ? Date.parse(a) : 0) - (b ? Date.parse(b) : 0);
}
